import { useState } from 'react';
import { Alert, Badge, Button, Center, Group, Loader, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconHeadset, IconPhone, IconPhoneIncoming, IconPhoneOff } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { DEV_SWITCHER_OFFSET } from '@/components/dev-user-switcher';
import { useMe } from '@/hooks/use-me';
import { useOrg } from '@/hooks/use-org';
import { useSoftphone } from '@/hooks/use-softphone';
import { ActiveCallView } from './-components/agent/active-call-view';
import { ActivityDrawer } from './-components/agent/activity-drawer';
import { AgentSidebar } from './-components/agent/agent-sidebar';
import {
  AgentWorkspaceMembershipSubscription,
  AgentWorkspaceOrganizationSubscription,
} from './-components/agent/graphql';
import { QueuePanel } from './-components/agent/queue-panel';
import type { DirectCallSnapshot, WorkspaceQueue } from './-components/agent/types';
import { OrganizationPhoneNumbersSubscription } from './-components/call-graphql';
import { formatPhone, toMs } from './-components/format';
import { useNow } from './-components/use-now';

export const Route = createFileRoute('/_authed/agent')({
  component: AgentWorkspacePage,
});

function WrapUpView({ wrapUpEndsAt, onReady }: { wrapUpEndsAt: number | null; onReady: () => Promise<void> }) {
  const now = useNow();
  const remaining = wrapUpEndsAt === null ? null : Math.max(0, Math.ceil((wrapUpEndsAt - now) / 1000));

  return (
    <Center className="flex-1">
      <Stack align="center" gap="lg">
        <Badge
          size="xl"
          color="yellow"
          variant="light"
          radius="md"
          styles={{ label: { fontSize: 'var(--mantine-font-size-lg)', padding: '4px 12px' } }}
        >
          Wrap Up {remaining !== null ? `— ${remaining}s` : ''}
        </Badge>
        <Text c="dimmed">Complete your post-call notes</Text>
        <Button color="green" size="lg" onClick={() => void onReady()}>
          Ready for Next Call
        </Button>
      </Stack>
    </Center>
  );
}

function IncomingCallView({
  callerName,
  callerNumber,
  onAnswer,
  onReject,
}: {
  callerName: string | null;
  callerNumber: string | null;
  onAnswer: () => void;
  onReject: () => void;
}) {
  return (
    <Center className="flex-1">
      <Stack align="center" gap="xl">
        <div className="animate-pulse">
          <IconPhoneIncoming size={64} color="var(--mantine-color-green-6)" />
        </div>
        <Stack align="center" gap="xs">
          <Text size="xl" fw={700}>
            Incoming Call
          </Text>
          <Text size="lg" ff="monospace" fw={500}>
            {callerNumber ? formatPhone(callerNumber) : 'Unknown'}
          </Text>
          {callerName && (
            <Text size="md" c="dimmed">
              {callerName}
            </Text>
          )}
        </Stack>
        <Group gap="lg">
          <Button
            size="xl"
            color="green"
            leftSection={<IconPhone size={24} />}
            radius="xl"
            onClick={onAnswer}
            style={{ minWidth: 160 }}
          >
            Answer
          </Button>
          <Button
            size="xl"
            color="red"
            variant="light"
            leftSection={<IconPhoneOff size={24} />}
            radius="xl"
            onClick={onReject}
            style={{ minWidth: 160 }}
          >
            Reject
          </Button>
        </Group>
      </Stack>
    </Center>
  );
}

function OfflineView() {
  return (
    <Center className="flex-1">
      <Stack align="center" gap="md">
        <IconHeadset size={48} color="var(--mantine-color-dimmed)" />
        <Text c="dimmed" size="lg">
          Go online to start taking calls
        </Text>
      </Stack>
    </Center>
  );
}

function RestoringCallView() {
  return (
    <Center className="flex-1">
      <Stack align="center" gap="md">
        <Loader size="sm" />
        <Text c="dimmed" size="lg">
          Restoring call state
        </Text>
      </Stack>
    </Center>
  );
}

function RecoveredDirectCallView({
  call,
  isReconnecting,
  onReconnect,
  onHangup,
}: {
  call: DirectCallSnapshot;
  isReconnecting: boolean;
  onReconnect: () => void;
  onHangup: () => void;
}) {
  return (
    <Center className="flex-1">
      <Stack align="center" gap="lg">
        <Badge size="lg" color="blue" variant="light">
          {call.direction === 'inbound' ? 'Recovered Call' : 'Recovered Outbound Call'}
        </Badge>
        <Stack align="center" gap="xs">
          <Text size="xl" fw={700}>
            {call.remoteCallerNumber ? formatPhone(call.remoteCallerNumber) : 'Active call'}
          </Text>
          {call.remoteCallerName && <Text c="dimmed">{call.remoteCallerName}</Text>}
          <Text c="dimmed" ta="center">
            Reconnect the softphone to restore live call controls.
          </Text>
        </Stack>
        <Group>
          <Button variant="light" onClick={onReconnect} loading={isReconnecting}>
            Reconnect Call
          </Button>
          <Button
            color="red"
            leftSection={<IconPhoneOff size={16} />}
            onClick={onHangup}
            disabled={!call.callControlId}
          >
            End Call
          </Button>
        </Group>
        {!call.callControlId && (
          <Text size="sm" c="dimmed" ta="center">
            Remote hangup becomes available after Call Control reconnects.
          </Text>
        )}
      </Stack>
    </Center>
  );
}

function AgentWorkspace({ organizationId, userId }: { organizationId: string; userId: string }) {
  const {
    isConnected,
    isConnecting,
    activeCall,
    connect,
    answerCall,
    rejectCall,
    hangupCall,
    lastError,
    clearLastError,
    endWrapUp,
    statusLocked,
    agentStatus,
    wrapUpEndsAt,
  } = useSoftphone();

  const [selectedQueueId, setSelectedQueueId] = useState<string | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const now = useNow();

  const [{ data: membershipData }] = useSubscription({
    query: AgentWorkspaceMembershipSubscription,
    variables: { organizationId, userId },
  });
  const [{ data: organizationData }] = useSubscription({
    query: AgentWorkspaceOrganizationSubscription,
    variables: { organizationId },
  });
  const [{ data: phoneData }] = useSubscription({
    query: OrganizationPhoneNumbersSubscription,
    variables: { organizationId },
  });

  const membership = membershipData?.organizationMembers[0] ?? null;

  if (!membershipData || !organizationData) {
    return (
      <Center style={{ height: `calc(100dvh - 60px - ${DEV_SWITCHER_OFFSET})` }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!membership) {
    return (
      <Center style={{ height: `calc(100dvh - 60px - ${DEV_SWITCHER_OFFSET})` }}>
        <Text c="dimmed">Not a member of this organization</Text>
      </Center>
    );
  }

  const status = membership.agentStatuses[0] ?? null;
  const queueEntry = status?.currentQueueEntry ?? null;
  const callSession = status?.currentAgentCallSession ?? null;

  const queues: WorkspaceQueue[] = membership.queueMembers.map((queueMember) => {
    const waitingCount = queueMember.queue.waiting.aggregate?.count ?? 0;
    const longestWaitStart = queueMember.queue.waiting.aggregate?.min?.enteredAt ?? null;

    return {
      id: queueMember.queue.id,
      name: queueMember.queue.name,
      deliveryMode: queueMember.queue.deliveryMode === 'push' ? 'push' : 'pull',
      isEscalation: queueMember.isEscalation ?? false,
      waitingCount,
      ringingCount: queueMember.queue.ringing.aggregate?.count ?? 0,
      longestWaitSecs: longestWaitStart ? Math.max(0, Math.round((now - toMs(longestWaitStart)) / 1000)) : 0,
    };
  });

  const recoveredDirectCall: DirectCallSnapshot | null =
    !activeCall &&
    !queueEntry &&
    callSession &&
    callSession.source === 'direct' &&
    ['ringing', 'active'].includes(callSession.status)
      ? {
          source: 'direct',
          agentCallSessionId: callSession.id,
          callControlId: callSession.callControlId,
          direction: callSession.direction === 'outbound' ? 'outbound' : 'inbound',
          state: callSession.status,
          remoteCallerName: callSession.remoteCallerName,
          remoteCallerNumber: callSession.remoteCallerNumber,
          startedAt: callSession.answeredAt ?? callSession.startedAt,
        }
      : null;

  const resolvedActiveCall = activeCall
    ? {
        ...activeCall,
        queueEntryId: activeCall.queueEntryId ?? queueEntry?.id ?? null,
        sessionVariables:
          activeCall.sessionVariables ?? ((queueEntry?.sessionVariables ?? null) as Record<string, string> | null),
      }
    : null;

  const effectiveQueueId =
    selectedQueueId && queues.some((q) => q.id === selectedQueueId) ? selectedQueueId : (queues[0]?.id ?? null);

  const isIncomingRing = resolvedActiveCall?.state === 'ringing' && resolvedActiveCall.direction === 'inbound';
  const isOnCall = resolvedActiveCall !== null && !isIncomingRing;
  const isWrapUp = agentStatus === 'wrap_up' && !resolvedActiveCall;
  const isRestoringCall = isConnected && statusLocked && !resolvedActiveCall && !recoveredDirectCall && !isWrapUp;
  const isIdle = isConnected && !statusLocked && !resolvedActiveCall && !isWrapUp;

  return (
    <div className="flex" style={{ height: `calc(100dvh - 60px - ${DEV_SWITCHER_OFFSET})` }}>
      <AgentSidebar
        email={membership.email}
        extensionNumber={membership.extensionNumber}
        queues={queues}
        phoneNumbers={phoneData?.phoneNumbers ?? []}
        outboundEnabled={
          !(organizationData.organizationsByPk?.outboundCallingLocked ?? false) &&
          (organizationData.organizationsByPk?.outboundCallingEnabled ?? true)
        }
        selectedQueueId={effectiveQueueId}
        onQueueSelect={setSelectedQueueId}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        {lastError && (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertCircle size={16} />}
            withCloseButton
            onClose={clearLastError}
            radius={0}
            className="shrink-0"
          >
            {lastError}
          </Alert>
        )}

        {!isConnected && !recoveredDirectCall && <OfflineView />}
        {isIncomingRing && resolvedActiveCall && (
          <IncomingCallView
            callerName={resolvedActiveCall.remoteCallerName}
            callerNumber={resolvedActiveCall.remoteCallerNumber}
            onAnswer={answerCall}
            onReject={rejectCall}
          />
        )}
        {isOnCall && resolvedActiveCall && <ActiveCallView activeCall={resolvedActiveCall} />}
        {recoveredDirectCall && (
          <RecoveredDirectCallView
            call={recoveredDirectCall}
            isReconnecting={isConnecting}
            onReconnect={() => void connect()}
            onHangup={hangupCall}
          />
        )}
        {isWrapUp && <WrapUpView wrapUpEndsAt={wrapUpEndsAt} onReady={endWrapUp} />}
        {isRestoringCall && <RestoringCallView />}
        {isIdle && (
          <QueuePanel
            organizationId={organizationId}
            queues={queues}
            selectedQueueId={effectiveQueueId}
            onQueueSelect={setSelectedQueueId}
          />
        )}

        <ActivityDrawer
          activity={membership.agentActivity}
          isOpen={activityOpen}
          onToggle={() => setActivityOpen((v) => !v)}
        />
      </div>
    </div>
  );
}

function AgentWorkspacePage() {
  const { organizationId, isLoading } = useOrg();
  const { me } = useMe();

  if (isLoading) {
    return (
      <Center style={{ height: `calc(100dvh - 60px - ${DEV_SWITCHER_OFFSET})` }}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId) {
    return (
      <Center style={{ height: `calc(100dvh - 60px - ${DEV_SWITCHER_OFFSET})` }}>
        <Text c="dimmed">No organization selected</Text>
      </Center>
    );
  }

  return <AgentWorkspace organizationId={organizationId} userId={me.id} />;
}
